import { apiCall } from './api';
import type { SiteTreeNode } from './siteTree';
import { collectSiteIdsFromTree } from './siteTree';

export interface ConfigAccount {
  id: string;
  source_system: string;
  username?: string | null;
  username_secret_ref?: string | null;
  site_ids: string | null;
  discovered_site_ids?: string | null;
  enabled: boolean;
}

export function extractSiteIds(account: ConfigAccount | undefined): number[] {
  if (!account) return [];

  const combined = [account.site_ids, account.discovered_site_ids]
    .filter((value): value is string => !!value)
    .join(',');

  if (!combined) return [];

  return Array.from(
    new Set(
      combined
        .split(',')
        .map((value) => parseInt(value.trim(), 10))
        .filter((value) => !Number.isNaN(value))
    )
  ).sort((left, right) => left - right);
}

export function buildAvailableSiteIds(
  authSites: number[],
  configAccounts: ConfigAccount[],
  selectedAccountId: string,
  /**
   * Optional Nova organization tree loaded for the currently selected account.
   * Used as a fallback when the account has no explicit `site_ids` /
   * `discovered_site_ids` stored — the tree itself becomes the access set.
   */
  selectedAccountSiteTree: SiteTreeNode[] = []
): number[] {
  const siteSet = new Set<number>();

  if (selectedAccountId) {
    const account = configAccounts.find((acct) => acct.id === selectedAccountId);
    const explicit = extractSiteIds(account);
    if (explicit.length > 0) {
      explicit.forEach((siteId) => siteSet.add(siteId));
    } else {
      collectSiteIdsFromTree(selectedAccountSiteTree).forEach((siteId) => siteSet.add(siteId));
    }
  } else {
    authSites.forEach((siteId) => siteSet.add(siteId));
    configAccounts.forEach((account) => {
      extractSiteIds(account).forEach((siteId) => siteSet.add(siteId));
    });
  }

  return Array.from(siteSet).sort((left, right) => left - right);
}

export function resolveNovaAccountIdForSite(
  configAccounts: ConfigAccount[],
  selectedAccountId: string,
  siteId: number | null
): string | null {
  if (selectedAccountId) {
    return selectedAccountId;
  }
  if (!siteId) {
    return null;
  }

  const matchingAccounts = configAccounts
    .filter((account) => extractSiteIds(account).includes(siteId))
    .map((account) => account.id);

  return matchingAccounts.length === 1 ? matchingAccounts[0] : null;
}

export function accountDisplayName(account: ConfigAccount): string {
  const username = (account.username || '').trim();
  if (username) return username;
  if (account.username_secret_ref) return account.username_secret_ref;
  return account.id;
}

export function accountDisplayLabel(account: ConfigAccount): string {
  const name = accountDisplayName(account);
  const siteCount = extractSiteIds(account).length;
  if (siteCount === 0) return name;
  return `${name} (${siteCount} site${siteCount === 1 ? '' : 's'})`;
}

export async function loadEnabledConfigAccounts(): Promise<ConfigAccount[]> {
  const response = await apiCall('/api/config/accounts');
  if (!response.ok) {
    throw new Error('Failed to load Nova accounts');
  }

  const data = await response.json();
  return (data.accounts || []).filter((account: ConfigAccount) => account.enabled);
}

export async function loadAccountSiteTree(accountId: string): Promise<SiteTreeNode[]> {
  const response = await apiCall(`/api/config/accounts/${encodeURIComponent(accountId)}/site-tree`);
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error || 'Failed to load Nova organization tree');
  }

  const data = await response.json();
  return Array.isArray(data.tree) ? data.tree : [];
}
